import { useState } from 'react'
import { fmt, plans } from '../../lib/geo'
import { PLAN_ICON } from '../../lib/content'
import { ActivityIcon } from '../ui/Icon'
import { Faces } from '../ui/Faces'
import { Reveal } from '../ui/Reveal'
import { SectionHead } from '../ui/SectionHead'
import './Features.css'

const DAYS = {
  Tonight: ['p2', 'p7', 'p6', 'p4', 'p15'],
  Tomorrow: ['p11', 'p13', 'p3', 'p10', 'p20'],
  'This week': ['p23', 'p21', 'p24', 'p16', 'p18'],
} as const
type Day = keyof typeof DAYS
const byId = Object.fromEntries(plans.map((p) => [p.id, p]))

function TonightList() {
  const [day, setDay] = useState<Day>('Tonight')
  return (
    <>
      <div className="seg" role="group" aria-label="Day">
        {(Object.keys(DAYS) as Day[]).map((d) => (
          <button key={d} type="button" aria-pressed={day === d} onClick={() => setDay(d)}>{d}</button>
        ))}
      </div>
      <div className="list">
        {DAYS[day].map((id) => {
          const p = byId[id]
          if (!p) return null
          const left = p.spots - p.filled
          return (
            <div className="li" key={id}>
              <span className="ico"><ActivityIcon name={PLAN_ICON[id] ?? 'chai'} /></span>
              <div><b>{p.title}</b><small>{fmt(p.start)}, {p.venue.area}</small></div>
              <span className="left">{left > 0 ? `${left} left` : 'full'}</span>
            </div>
          )
        })}
      </div>
    </>
  )
}

function Court() {
  return (
    <svg className="court" viewBox="0 0 300 150" role="img" aria-label="Badminton court with three players and one open spot">
      <rect x="6" y="6" width="288" height="138" rx="10" fill="rgba(255,242,218,.04)" stroke="rgba(255,236,204,.3)" />
      <path d="M150 6v138M6 75h288M60 6v138M240 6v138" stroke="rgba(255,236,204,.18)" fill="none" />
      <path d="M150 0v150" stroke="rgba(248,211,115,.7)" strokeWidth="2" strokeDasharray="3 4" />
      <circle cx="100" cy="42" r="15" fill="#F8D373" /><circle cx="100" cy="108" r="15" fill="#E3A92F" /><circle cx="200" cy="42" r="15" fill="#EE7F23" />
      <circle cx="200" cy="108" r="15" fill="none" stroke="#F8D373" strokeWidth="2" strokeDasharray="4 4" className="open-spot" />
      <text x="200" y="113" textAnchor="middle" fontFamily="Funnel Sans, sans-serif" fontWeight="700" fontSize="14" fill="#F8D373">you?</text>
    </svg>
  )
}

const COMPOSE = [
  { icon: 'badminton', label: 'Badminton', title: 'Badminton doubles', place: 'Smash Arena', open: '1 spot open' },
  { icon: 'guitar', label: 'Jam', title: 'Acoustic jam', place: 'The Chai Adda', open: 'Cajón open' },
  { icon: 'chai', label: 'Chai', title: 'Chai and a chat', place: 'Khan Market', open: '3 seats open' },
] as const
const WHEN = ['Tonight, 8 PM', 'Tomorrow, 7 PM', 'Sunday, 9 AM'] as const

/** A tiny, working version of posting a plan: pick what and when, see the plan card it makes. */
function PlanComposer() {
  const [what, setWhat] = useState(0)
  const [when, setWhen] = useState(0)
  const c = COMPOSE[what]
  return (
    <div className="composer">
      <div className="pick" role="group" aria-label="What">
        {COMPOSE.map((o, i) => (
          <button key={o.label} type="button" className="pickchip" aria-pressed={what === i} onClick={() => setWhat(i)}>
            <span className="pi"><ActivityIcon name={o.icon} stroke="currentColor" accent="currentColor" /></span>{o.label}
          </button>
        ))}
      </div>
      <div className="pick" role="group" aria-label="When">
        {WHEN.map((w, i) => (
          <button key={w} type="button" className="pickchip small" aria-pressed={when === i} onClick={() => setWhen(i)}>{w}</button>
        ))}
      </div>
      <div className="preview" aria-live="polite">
        <span className="ico"><ActivityIcon name={c.icon} /></span>
        <div className="min0"><b>{c.title}</b><small>{WHEN[when]} · {c.place}</small></div>
        <span className="left">{c.open}</span>
      </div>
    </div>
  )
}

export function Features() {
  return (
    <section>
      <div className="wrap">
        <SectionHead eyebrow="The App" title={<>And everything <em className="s">around</em> the map.</>}>
          What's on tonight, the plan before you join, the people you keep meeting, and posting your own plan in under a minute.
        </SectionHead>
        <div className="bento">
          <Reveal className="tile glass t1"><h3>Tonight near Hauz Khas</h3><p>Sorted by what starts first and how many spots are left.</p><TonightList /></Reveal>
          <Reveal className="tile glass t2"><h3>See the shape before you ask</h3><p>The open side of the court, the empty chair, the band that still needs a drummer.</p><Court /></Reveal>
          <Reveal className="tile glass t3">
            <h3>The plan, before you join</h3><p>Badminton doubles, tonight at 8:00 PM, Golf Course Road.</p>
            <dl className="kv"><dt>Level</dt><dd>Intermediate</dd><dt>Cost</dt><dd>₹150 each, court split</dd><dt>Host reliability</dt><dd><b className="hl">98</b> of 100</dd></dl>
            <p className="small">Court number and group chat unlock after Aditya approves you.</p>
          </Reveal>
          <Reveal className="tile glass t4"><h3>Your regulars</h3><p>The same faces, plan after plan.</p><div className="faces-row"><Faces names={['A', 'M', 'K', 'N']} size={44} /></div><div className="big">11<small>same faces</small></div></Reveal>
          <Reveal className="tile glass t5"><h3>Post a plan in seconds</h3><p>Pick what and when. mauj turns it into a plan people nearby can join.</p><PlanComposer /></Reveal>
          <Reveal className="tile glass amber t6"><h3>The chat opens when you're in</h3><p>Once the host approves you, a group chat opens with the exact spot and everyone who's going.</p><div className="chips-row"><span className="chip">Exact spot</span> <span className="chip">Group chat</span> <span className="chip">Reminders</span></div></Reveal>
        </div>
      </div>
    </section>
  )
}
